import { showBackofficeFlash } from "packs/init-backoffice-flashes"
import { upsertContentDocument } from "packs/init-backoffice-content-documents"
import { markBackofficeFormClean } from "packs/init-backoffice-unsaved-warn"

const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")

const isManualSave = (form) => form.dataset.autosaveManual === "true"

// Contenus du site : pas de flash autosave (enregistrements manuels / flashes dédiés).
const isQuietSave = (form) =>
  form?.dataset.autosaveQuiet === "true" ||
  form?.classList.contains("backoffice-form--quiet-autosave")

// Inclut / force les champs hors <form> (attribut form="…"), ex. prestations.
// new FormData(form) les inclut en principe ; on réécrit via set() pour être sûr
// sans dupliquer les clés (sinon Rack peut recevoir des tableaux).
const buildAutosaveFormData = (form) => {
  const data = new FormData(form)
  if (!form?.id) return data

  document.querySelectorAll(`[form="${CSS.escape(form.id)}"]`).forEach((el) => {
    if (
      !(
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement
      )
    ) {
      return
    }
    if (el.disabled || !el.name) return

    if (el instanceof HTMLInputElement && (el.type === "checkbox" || el.type === "radio")) {
      if (el.checked) data.set(el.name, el.value || "1")
      return
    }

    if (el instanceof HTMLInputElement && el.type === "file") return

    data.set(el.name, el.value)
  })

  return data
}

const refreshContentImagePreviews = (form) => {
  form.querySelectorAll('input[type="file"]').forEach((input) => {
    const file = input.files && input.files[0]
    if (!file || !file.type.startsWith("image/")) return

    const row = input.closest(".backoffice-content-media-row")
    if (!row) return

    const objectUrl = URL.createObjectURL(file)
    let preview = row.querySelector(".backoffice-content-preview")
    let img = preview && preview.querySelector("img")

    if (!img) {
      preview = document.createElement("div")
      preview.className = "backoffice-content-preview"
      const link = document.createElement("a")
      link.target = "_blank"
      link.rel = "noopener"
      img = document.createElement("img")
      link.appendChild(img)
      preview.appendChild(link)
      row.insertBefore(preview, row.firstChild)
    }

    img.src = objectUrl
    img.alt = file.name

    row.querySelector(".backoffice-muted")?.remove()
  })
}

const clearEmbeddedDocumentInput = (form) => {
  const input = form.querySelector('input[name="group_document[file]"]')
  if (!input) return
  input.value = ""
  const wrap = input.closest(".backoffice-file")
  const name = wrap?.querySelector(".backoffice-file__name")
  if (name) {
    name.textContent = "Aucun fichier choisi"
    name.classList.remove("is-filled")
  }
}

const submitButtonsFor = (form) => {
  const buttons = Array.from(form.querySelectorAll('button[type="submit"], input[type="submit"]'))
  if (form.id) {
    document
      .querySelectorAll(`button[type="submit"][form="${form.id}"], input[type="submit"][form="${form.id}"]`)
      .forEach((button) => buttons.push(button))
  }
  return buttons
}

const enableSubmitButtons = (form) => {
  submitButtonsFor(form).forEach((button) => {
    button.disabled = false
    button.removeAttribute("disabled")
    if (button.dataset.disableWith != null && button.dataset.originalValue != null) {
      button.value = button.dataset.originalValue
    }
  })
}

const stripHtml = (value) =>
  (value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()

const syncContentGroupTitle = (form) => {
  const input = form.querySelector("[data-content-group-title-input]")
  const title = form.closest("details")?.querySelector("[data-content-group-title]")
  if (!input || !title) return

  const nextTitle = stripHtml(input.value)
  if (!nextTitle) return

  const prefix = title.dataset.contentGroupTitlePrefix || ""
  title.textContent = `${prefix}${nextTitle}`
}

const applyRegistrationStatuses = (statuses) => {
  if (!statuses) return

  Object.entries(statuses).forEach(([opening, data]) => {
    const windowEl = document.querySelector(`[data-registration-window][data-opening="${opening}"]`)
    const badge = windowEl?.querySelector("[data-registration-status]")
    if (!badge || !data) return
    badge.textContent = data.label
    badge.className = data.class
  })
}

const applySetupProgress = (percent) => {
  if (percent == null || Number.isNaN(Number(percent))) return

  const value = Math.round(Number(percent))
  const label = document.querySelector("[data-setup-progress-label]")
  const bar = document.querySelector("[data-setup-progress-bar]")
  if (label) label.textContent = `${value}% configuré`
  if (bar) bar.style.width = `${value}%`
}

export const applyWizardChecklist = (checklist, progressPercent) => {
  if (checklist) {
    Object.entries(checklist).forEach(([step, complete]) => {
      const item = document.querySelector(`[data-wizard-step="${step}"]`)
      if (!item) return
      item.classList.toggle("is-done", Boolean(complete))
    })

    const remindersNote = document.querySelector("[data-reminders-intro-note]")
    if (remindersNote && checklist.reminders !== undefined) {
      remindersNote.hidden = Boolean(checklist.reminders)
    }

    const contentNote = document.querySelector("[data-content-intro-note]")
    if (contentNote && checklist.content !== undefined) {
      contentNote.hidden = Boolean(checklist.content)
    }
  }

  applySetupProgress(progressPercent)
}

const applyGuides = (guides) => {
  if (!guides) return

  Object.entries(guides).forEach(([key, data]) => {
    const row = document.querySelector(`[data-guide="${key}"]`)
    if (!row || !data) return

    const attached = Boolean(data.attached)
    row.classList.toggle("is-disabled", !attached)

    const status = row.querySelector("[data-guide-status]")
    if (status) {
      status.textContent = attached ? "Chargé" : "À charger"
      status.className = attached
        ? "backoffice-status backoffice-status--open"
        : "backoffice-status backoffice-status--todo"
    }

    const checkbox = row.querySelector('input[type="checkbox"][name*="documents_checklist"]')
    const control = row.querySelector(".backoffice-content-checklist__control")
    const year =
      row.closest("[data-edition-year]")?.dataset?.editionYear ||
      document.querySelector("[data-edition-year]")?.dataset?.editionYear ||
      ""
    if (checkbox) {
      checkbox.disabled = !attached
      if (!attached) checkbox.checked = false
    }
    if (control) {
      if (!attached) {
        control.removeAttribute("data-tooltip")
      } else if (checkbox?.checked) {
        control.setAttribute("data-tooltip", `Guide validé pour l'édition ${year} ! 🎉`)
      } else {
        control.setAttribute("data-tooltip", "Vérifiez le guide, puis cochez pour confirmer.")
      }
    }

    const link = row.querySelector("[data-guide-link]")
    if (link) {
      if (attached && data.url) {
        link.href = data.url
        link.textContent = data.filename || "Télécharger"
        link.hidden = false
      } else {
        link.removeAttribute("href")
        link.textContent = ""
        link.hidden = true
      }
    }

    const input = row.querySelector('input[type="file"]')
    if (input) input.value = ""
    const fileName = row.querySelector(".backoffice-file__name")
    if (fileName) {
      fileName.textContent = "Aucun fichier choisi"
      fileName.classList.remove("is-filled")
    }
  })
}

const DOCUMENT_CHECKLIST_TOOLTIP =
  "Vérifiez que le document est à jour, puis cochez la ligne pour confirmer."

const CONTENT_CHECKLIST_TOOLTIP =
  "Mettez à jour ce contenu, puis cochez pour confirmer."

const editionYearFrom = (el) =>
  el?.closest?.("[data-edition-year]")?.dataset?.editionYear ||
  document.querySelector("[data-edition-year]")?.dataset?.editionYear ||
  ""

const documentConfirmedTooltip = (year = editionYearFrom()) =>
  `Document validé pour l'édition ${year} ! 🎉`

const contentConfirmedTooltip = (year = editionYearFrom()) =>
  `Contenu validé pour l'édition ${year} ! 🎉`

const reminderConfirmedTooltip = (year = editionYearFrom()) =>
  `Notification validée pour l'édition ${year} ! 🎉`

const pricingChecklistTooltipForForm = (form) => {
  if (form?.classList.contains("backoffice-form--amenities")) {
    return "Vérifiez la prestation, puis cochez pour confirmer."
  }
  if (form?.classList.contains("backoffice-form--reminders")) {
    return "Vérifiez la notification, puis cochez pour confirmer."
  }

  return "Vérifiez cette information, puis cochez pour confirmer."
}

const pricingChecklistConfirmedTooltipForForm = (form) => {
  const year = form?.dataset?.editionYear || editionYearFrom(form) || ""
  if (form?.classList.contains("backoffice-form--reminders")) {
    return reminderConfirmedTooltip(year)
  }
  return `Information confirmée pour l'édition ${year} ! 🎉`
}

const syncPricingChecklistTooltip = (control, form = null) => {
  if (!control) return

  const checkbox = control.querySelector('input[type="checkbox"]')
  if (!checkbox) return

  const scope = form || control.closest("form")
  control.setAttribute(
    "data-tooltip",
    checkbox.checked
      ? pricingChecklistConfirmedTooltipForForm(scope)
      : pricingChecklistTooltipForForm(scope)
  )
}

const dismissChecklistTooltipUntilLeave = (control) => {
  if (!control?.hasAttribute?.("data-tooltip")) return

  control.classList.add("is-tooltip-dismissed")
  control.querySelector('input[type="checkbox"]')?.blur()

  const clear = () => {
    control.classList.remove("is-tooltip-dismissed")
    control.removeEventListener("pointerleave", clear)
  }
  control.addEventListener("pointerleave", clear)
}

const isSetupChecklistCheckbox = (el) =>
  el?.matches?.(
    'input[type="checkbox"][name*="tarifs_checklist"], input[type="checkbox"][name*="date_and_location_checklist"], input[type="checkbox"][name*="calendar_checklist"], input[type="checkbox"][name*="reminders_checklist"], input[type="checkbox"][name*="documents_checklist"], input[type="checkbox"][name*="contenu_checklist"]'
  )

const isChecklistControlElement = (el) =>
  !!el?.closest?.(
    ".backoffice-content-checklist__control, .backoffice-pricing-checklist__control"
  )

// Clic sur une coche : le change/blur du champ encore focusé ne doit pas
// re-cocher/décocher juste avant le toggle du clic (sinon la coche « ne prend pas »).
let checklistPointerArmed = false
let checklistPointerClearTimer = null
// Clics récents : évite qu’une réponse autosave périmée réécrase la coche.
const localChecklistOverrides = new Map()

const rememberChecklistOverride = (checkbox) => {
  if (!checkbox?.name) return
  localChecklistOverrides.set(checkbox.name, {
    checked: Boolean(checkbox.checked),
    expires: Date.now() + 2500,
  })
}

const armChecklistPointer = () => {
  checklistPointerArmed = true
  if (checklistPointerClearTimer) clearTimeout(checklistPointerClearTimer)
  checklistPointerClearTimer = setTimeout(() => {
    checklistPointerArmed = false
    checklistPointerClearTimer = null
  }, 500)
}

const bindChecklistPointerGuard = () => {
  if (document.documentElement.dataset.checklistPointerGuardBound === "true") return
  document.documentElement.dataset.checklistPointerGuardBound = "true"

  document.addEventListener(
    "pointerdown",
    (event) => {
      if (isChecklistControlElement(event.target)) armChecklistPointer()
    },
    true
  )

  // Filet de sécurité : si le clic arrive sur le contrôle / la marque (pas directement
  // sur l’input), bascule la coche manuellement. Couvre les cas où le CSS
  // pointer-events laisse l’input inactif alors que la pastille est visible.
  document.addEventListener(
    "click",
    (event) => {
      if (!event.target.closest?.(".is-checklist-mode")) return

      const control = event.target.closest?.(
        ".backoffice-pricing-checklist__control, .backoffice-content-checklist__control"
      )
      if (!control) return

      const checkbox = control.querySelector('input[type="checkbox"]')
      if (!checkbox || checkbox.disabled) return
      if (event.target === checkbox) {
        rememberChecklistOverride(checkbox)
        return
      }

      event.preventDefault()
      checkbox.checked = !checkbox.checked
      rememberChecklistOverride(checkbox)
      checkbox.dispatchEvent(new Event("input", { bubbles: true }))
      checkbox.dispatchEvent(new Event("change", { bubbles: true }))
    },
    true
  )
}

const setupChecklistCheckboxSelectorForKey = (key) =>
  `input[type="checkbox"][name="edition[tarifs_checklist][${key}]"], input[type="checkbox"][name="edition[date_and_location_checklist][${key}]"], input[type="checkbox"][name="edition[calendar_checklist][${key}]"], input[type="checkbox"][name="edition[reminders_checklist][${key}]"]`

const isPricingChecklistForm = (form) =>
  form?.classList.contains("backoffice-form--payment") ||
  form?.classList.contains("backoffice-form--amenities") ||
  form?.classList.contains("backoffice-form--date-and-location") ||
  form?.classList.contains("backoffice-form--calendar") ||
  form?.classList.contains("backoffice-form--orders") ||
  form?.classList.contains("backoffice-form--reminders")

const resolveAutosaveForm = (field) => {
  if (!field) return null

  const closest = field.closest?.("form[data-autosave]")
  if (closest) return closest

  const formId = field.getAttribute?.("form")
  if (formId) {
    const owned = document.getElementById(formId)
    if (owned?.matches?.("form[data-autosave]")) return owned
  }

  return null
}

const syncPricingIntroNote = (form) => {
  if (!form) return

  // La note paiement est gérée par le mode checklist, pas par l'état des coches.
  if (form.classList.contains("backoffice-form--payment")) return

  const note =
    form.classList.contains("backoffice-form--amenities") &&
    document.querySelector("[data-amenities-intro-note]")

  if (!note) return

  const checkboxes = form.id
    ? document.querySelectorAll(
        `input[type="checkbox"][name*="tarifs_checklist"][form="${form.id}"], form#${CSS.escape(form.id)} input[type="checkbox"][name*="tarifs_checklist"]`
      )
    : form.querySelectorAll('input[type="checkbox"][name*="tarifs_checklist"]')
  note.hidden = checkboxes.length > 0 && Array.from(checkboxes).every((box) => box.checked)
}

const syncContentIntroNote = (form) => {
  const note = document.querySelector("[data-content-intro-note]")
  if (!note || !form) return

  const checkboxes = form.querySelectorAll('input[type="checkbox"][name*="contenu_checklist"]')
  if (!checkboxes.length) return

  note.hidden = Array.from(checkboxes).every((box) => box.checked)
}

const confirmPricingCheckbox = (control) => {
  if (!control) return false

  const checkbox = control.querySelector('input[type="checkbox"]')
  if (!checkbox || checkbox.checked) return false

  checkbox.checked = true
  syncPricingChecklistTooltip(control)

  syncPricingIntroNote(control.closest("form"))

  return true
}

const unconfirmPricingCheckbox = (control, form) => {
  if (!control) return false

  const checkbox = control.querySelector('input[type="checkbox"]')
  if (!checkbox || !checkbox.checked) return false

  checkbox.checked = false
  syncPricingChecklistTooltip(control, form)
  syncPricingIntroNote(form)

  return true
}

const findPricingChecklistControl = (field) => {
  const row = field.closest(".backoffice-amenity-row")
  if (row) return row.querySelector(".backoffice-pricing-checklist__control")

  const checklistScope = field.closest(
    "[data-pricing-checklist-key], .backoffice-payment-check-row, .backoffice-payment-rule-row, .backoffice-window.backoffice-pricing-section"
  )
  if (!checklistScope) return null

  let control = checklistScope.querySelector(".backoffice-pricing-checklist__control")
  if (!control) {
    const checkRow = checklistScope.closest(".backoffice-payment-check-row")
    control = checkRow?.querySelector(".backoffice-pricing-checklist__control")
  }
  if (!control && checklistScope.dataset.pricingChecklistKey) {
    const checkbox = document.querySelector(
      setupChecklistCheckboxSelectorForKey(checklistScope.dataset.pricingChecklistKey)
    )
    control = checkbox?.closest(".backoffice-pricing-checklist__control")
  }

  return control
}

const isPricingFieldEmpty = (field) => {
  if (!field || field.disabled) return false
  if (field.type === "checkbox" || field.type === "radio" || field.type === "file") return false
  if (field.matches("[data-setup-checklist-mode]")) return false

  return field.value.toString().trim() === ""
}

const isReminderSendingDatesEmpty = (field) => {
  const row = field.closest(".backoffice-reminder-check-row")
  if (!row) return isPricingFieldEmpty(field)

  const dates = row.querySelectorAll('[name*="[email_sending_date_"]')
  if (!dates.length) return isPricingFieldEmpty(field)

  return Array.from(dates).every((input) => input.value.toString().trim() === "")
}

const confirmPricingChecklistForField = (field, form) => {
  if (!isPricingChecklistForm(form)) return false
  if (isSetupChecklistCheckbox(field)) return false
  // Blur/change d’un champ pendant un clic sur la coche : ne pas toucher à la coche.
  if (checklistPointerArmed || isChecklistControlElement(document.activeElement)) return false

  const control = findPricingChecklistControl(field)
  if (!control) return false

  const empty = form.classList.contains("backoffice-form--reminders")
    ? isReminderSendingDatesEmpty(field)
    : isPricingFieldEmpty(field)

  if (empty) {
    return unconfirmPricingCheckbox(control, form)
  }

  return confirmPricingCheckbox(control)
}

export const syncPricingChecklistFromField = (field) => {
  const form = resolveAutosaveForm(field)
  if (!form) return false
  return confirmPricingChecklistForField(field, form)
}

const applyPricingChecklist = (items) => {
  if (!items) return

  items.forEach(({ key, confirmed }) => {
    const checkbox = document.querySelector(setupChecklistCheckboxSelectorForKey(key))
    const control = checkbox?.closest(".backoffice-pricing-checklist__control")
    if (!checkbox || !control) return

    const override = localChecklistOverrides.get(checkbox.name)
    if (override && Date.now() < override.expires) {
      // Garde l’état local du clic tant que la réponse serveur n’est pas alignée.
      if (Boolean(confirmed) === override.checked) {
        localChecklistOverrides.delete(checkbox.name)
      } else {
        checkbox.checked = override.checked
        syncPricingChecklistTooltip(control, resolveAutosaveForm(checkbox))
        return
      }
    }

    checkbox.checked = Boolean(confirmed)
    syncPricingChecklistTooltip(control, checkbox.closest("form") || resolveAutosaveForm(checkbox))
  })

  const amenitiesNote = document.querySelector("[data-amenities-intro-note]")
  if (amenitiesNote && items.length) {
    const allConfirmed = items.every(({ key, confirmed }) => {
      const checkbox = document.querySelector(setupChecklistCheckboxSelectorForKey(key))
      if (checkbox) return checkbox.checked
      return confirmed
    })
    amenitiesNote.hidden = allConfirmed
  }
}

const formatDocumentUploadDate = (isoDate) => {
  if (!isoDate) return null

  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(isoDate))
}

const documentsStatusLabel = (present, confirmed, uploadedAtIso) => {
  if (!present) return "À charger"
  if (confirmed) return "Chargé et confirmé"

  const dateLabel = formatDocumentUploadDate(uploadedAtIso)
  if (dateLabel) return `Chargé le ${dateLabel} - validité du doc à confirmer`

  return "Validité du doc à confirmer"
}

const applyDocumentsChecklist = (items) => {
  if (!items) return

  items.forEach(({ key, present, confirmed, status_label, uploaded_at }) => {
    const li = document.querySelector(`[data-documents-checklist-key="${key}"]`)
    if (!li) return

    const uploadedAtIso = uploaded_at || li.dataset.documentsUploadedAt || null
    if (uploaded_at) li.dataset.documentsUploadedAt = uploaded_at

    const checkbox = li.querySelector('input[type="checkbox"][name*="documents_checklist"]')
    const status = li.querySelector("[data-documents-check-status]")
    const control = li.querySelector(".backoffice-content-checklist__control")

    li.classList.toggle("is-disabled", !present)

    if (checkbox) {
      checkbox.disabled = !present
      const override = localChecklistOverrides.get(checkbox.name)
      if (override && Date.now() < override.expires) {
        if (Boolean(confirmed) === override.checked) {
          localChecklistOverrides.delete(checkbox.name)
          checkbox.checked = Boolean(confirmed)
        } else {
          checkbox.checked = override.checked
        }
      } else {
        checkbox.checked = Boolean(confirmed)
      }
    }

    if (status) {
      status.textContent =
        status_label || documentsStatusLabel(present, confirmed, uploadedAtIso)
      if (!present) {
        status.className = "backoffice-status backoffice-status--todo"
      } else {
        status.className = confirmed
          ? "backoffice-status backoffice-status--open"
          : "backoffice-status backoffice-status--loaded"
      }
      status.dataset.documentsCheckStatus = "true"
    }

    if (control) {
      if (!present) {
        control.removeAttribute("data-tooltip")
      } else if (confirmed) {
        control.setAttribute("data-tooltip", documentConfirmedTooltip(editionYearFrom(control)))
      } else {
        control.setAttribute("data-tooltip", DOCUMENT_CHECKLIST_TOOLTIP)
      }
    }
  })
}

const refreshDocumentsChecklist = async () => {
  const form = document.querySelector(".backoffice-documents-checklist")?.closest("form")
  if (!form) return

  try {
    const response = await fetch(form.action, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    })
    if (!response.ok) return

    const data = await response.json()
    applyWizardUiState(data)
  } catch (_) {
    /* ignore background refresh errors */
  }
}

const bindDocumentsChecklistRefresh = () => {
  if (document.documentElement.dataset.documentsChecklistRefreshBound === "true") return
  document.documentElement.dataset.documentsChecklistRefreshBound = "true"

  document.addEventListener("visibilitychange", () => {
    if (!document.querySelector(".backoffice-documents-checklist")) return
    if (document.visibilityState === "visible") refreshDocumentsChecklist()
  })

  window.addEventListener("focus", () => {
    if (!document.querySelector(".backoffice-documents-checklist")) return
    refreshDocumentsChecklist()
  })
}

const applyWizardUiState = (data, { skipChecklists = false } = {}) => {
  applyRegistrationStatuses(data.registration_statuses)
  applyGuides(data.guides)

  // A newer save is already queued: don't clobber checkbox DOM with a stale response
  // (that race makes checklist clicks feel like they "didn't take").
  if (skipChecklists) return

  applyWizardChecklist(data.checklist, data.progress_percent)
  applyDocumentsChecklist(data.documents_checklist)
  applyPricingChecklist(data.tarifs_checklist)
  applyPricingChecklist(data.date_and_location_checklist)
  applyPricingChecklist(data.calendar_checklist)
  applyPricingChecklist(data.reminders_checklist)
}

const saveForm = async (form) => {
  if (!form.isConnected) return

  if (form.dataset.autosaving === "true") {
    form.dataset.autosaveQueued = "true"
    return
  }

  form.dataset.autosaving = "true"
  const saveGeneration = String(Number(form.dataset.autosaveGeneration || 0) + 1)
  form.dataset.autosaveGeneration = saveGeneration

  const reloadOnSuccess = form.dataset.autosaveReload === "true" && !isManualSave(form)
  delete form.dataset.autosaveReload

  try {
    const response = await fetch(form.action, {
      method: "POST",
      body: buildAutosaveFormData(form),
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": csrfToken() || "",
      },
      credentials: "same-origin",
    })

    let data = {}
    try {
      data = await response.json()
    } catch (_) {
      /* HTML redirect responses are fine */
    }

    if (!response.ok) {
      showBackofficeFlash(data.error || "Erreur d'enregistrement", "alert")
      return
    }

    if (reloadOnSuccess) {
      sessionStorage.setItem("backofficeFlash", data.notice || "Enregistré")
      window.location.reload()
      return
    }

    if (isManualSave(form)) {
      refreshContentImagePreviews(form)
      syncContentGroupTitle(form)
      if (data.document) {
        upsertContentDocument(form.querySelector("[data-content-docs]"), data.document, { replace: true })
        clearEmbeddedDocumentInput(form)
      }
      markBackofficeFormClean(form)
    }

    const queued = form.dataset.autosaveQueued === "true"
    const superseded =
      !form.isConnected || form.dataset.autosaveGeneration !== saveGeneration
    applyWizardUiState(data, {
      skipChecklists: superseded || queued,
    })
    if (!superseded && !queued && !isQuietSave(form)) {
      showBackofficeFlash(data.notice || "Enregistré", "notice")
    }
  } catch (_) {
    showBackofficeFlash("Erreur d'enregistrement", "alert")
  } finally {
    form.dataset.autosaving = "false"
    enableSubmitButtons(form)
    if (form.isConnected && form.dataset.autosaveQueued === "true") {
      delete form.dataset.autosaveQueued
      saveForm(form)
    }
  }
}

const scheduleSave = (form, delay = 450) => {
  clearTimeout(form._autosaveTimer)
  form._autosaveTimer = setTimeout(() => saveForm(form), delay)
}

export const triggerBackofficeAutosave = (form) => {
  if (!form || !form.matches("[data-autosave]")) return
  scheduleSave(form, 0)
}

const syncPricingTransferThreshold = (form) => {
  const toggle = form.querySelector("[data-pricing-transfer-toggle]")
  const threshold = form.querySelector("[data-pricing-transfer-threshold]")
  const row = form.querySelector("[data-pricing-transfer-threshold-row]")
  if (!toggle || !threshold) return

  const enabled = toggle.checked
  threshold.disabled = !enabled
  row?.classList.toggle("is-disabled", !enabled)
}

const formatFrDate = (date) => {
  const pad = (n) => String(n).padStart(2, "0")
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`
}

const formatFrDateTime = (date) => {
  const pad = (n) => String(n).padStart(2, "0")
  return `${formatFrDate(date)} à ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const syncOrderFormValidityDeadline = (form) => {
  form.querySelectorAll("[data-order-form-validity-preview]").forEach((root) => {
    const input = root.querySelector("[data-order-form-validity-days]")
    const target = root.querySelector("[data-order-form-validity-deadline]")
    if (!input || !target) return

    const days = Math.max(1, parseInt(input.value, 10) || 7)
    const emission = root.dataset.emissionDate
    if (!emission) return

    const [year, month, day] = emission.split("-").map(Number)
    if (!year || !month || !day) return

    const deadline = new Date(year, month - 1, day)
    deadline.setDate(deadline.getDate() + days)
    target.textContent = formatFrDate(deadline)
  })
}

const syncStartupOrderFormValidity = (form) => {
  form.querySelectorAll("[data-startup-order-form-validity-preview]").forEach((root) => {
    const input = root.querySelector("[data-startup-order-form-validity-days]")
    const label = root.querySelector("[data-startup-order-form-validity-label]")
    const deadline = root.querySelector("[data-startup-order-form-validity-deadline]")
    if (!input) return

    const days = Math.max(1, parseInt(input.value, 10) || 1)
    const unit = root.querySelector("[data-startup-order-form-validity-unit]")
    if (unit) unit.textContent = days === 1 ? "jour" : "jours"
    if (label) label.textContent = days === 1 ? "24 heures" : `${days} jours`

    const emissionAt = root.dataset.emissionAt
    if (!emissionAt || !deadline) return

    const expires = new Date(new Date(emissionAt).getTime() + days * 24 * 60 * 60 * 1000)
    if (Number.isNaN(expires.getTime())) return
    deadline.textContent = formatFrDateTime(expires)
  })
}

const bindAutosaveForm = (form) => {
  if (form.dataset.autosaveBound === "true") return
  form.dataset.autosaveBound = "true"

  if (form.classList.contains("backoffice-form--payment") || form.classList.contains("backoffice-form--orders")) {
    syncPricingTransferThreshold(form)
  }
  if (form.querySelector("[data-order-form-validity-preview]")) {
    syncOrderFormValidityDeadline(form)
  }
  if (form.querySelector("[data-startup-order-form-validity-preview]")) {
    syncStartupOrderFormValidity(form)
  }

  form.addEventListener("submit", (event) => {
    event.preventDefault()
    scheduleSave(form, 0)
  })

  form.addEventListener("input", (event) => {
    if (event.target.matches("[data-order-form-validity-days]")) {
      syncOrderFormValidityDeadline(form)
    }
    if (event.target.matches("[data-startup-order-form-validity-days]")) {
      syncStartupOrderFormValidity(form)
    }
  })

  if (isManualSave(form)) return

  form.addEventListener("input", (event) => {
    if (event.target.matches("[data-hall-select]")) return
    if (event.target.type === "file") return
    if (event.target.matches("[data-order-form-validity-days], [data-startup-order-form-validity-days]")) {
      // Preview already synced above; continue to autosave.
    }
    if (confirmPricingChecklistForField(event.target, form)) {
      scheduleSave(form, 0)
      return
    }
    scheduleSave(form)
  })

  form.addEventListener("change", (event) => {
    if (event.target.matches("[data-hall-select]")) return
    if (event.target.type === "file") {
      // Guide uploads update via JSON; other files may still need a reload.
      if (!event.target.matches("[data-guide-file]")) {
        form.dataset.autosaveReload = "true"
      }
    }

    if (confirmPricingChecklistForField(event.target, form)) {
      scheduleSave(form, 0)
      return
    }

    if (isSetupChecklistCheckbox(event.target)) {
      const control = event.target.closest(".backoffice-pricing-checklist__control")
      if (control) {
        syncPricingChecklistTooltip(control, form)
        dismissChecklistTooltipUntilLeave(control)
      }

      rememberChecklistOverride(event.target)
      syncPricingIntroNote(form)
    }

    if (event.target.matches("[data-pricing-transfer-toggle]")) {
      syncPricingTransferThreshold(form)
    }

    if (event.target.type === "checkbox") {
      const toggle = event.target.closest(".backoffice-toggle")
      const status = toggle?.querySelector("small")
      if (status) {
        status.textContent = event.target.checked
          ? (toggle.dataset.toggleStatusOn || "Ouvert")
          : (toggle.dataset.toggleStatusOff || "Fermé")
      }
      if (toggle) toggle.classList.toggle("is-on", event.target.checked)

      const contentStatus = event.target
        .closest(".backoffice-payment-check-row, .backoffice-content-checklist__item, li")
        ?.querySelector("[data-content-check-status]")
      if (contentStatus) {
        contentStatus.textContent = event.target.checked ? "Mis à jour" : "À mettre à jour"
      }

      if (event.target.matches('input[type="checkbox"][name*="contenu_checklist"]')) {
        const control = event.target.closest(".backoffice-content-checklist__control")
        if (control) {
          control.setAttribute(
            "data-tooltip",
            event.target.checked
              ? contentConfirmedTooltip(editionYearFrom(form))
              : CONTENT_CHECKLIST_TOOLTIP
          )
          dismissChecklistTooltipUntilLeave(control)
        }
        syncContentIntroNote(form)
      }

      const documentsLi = event.target.closest("[data-documents-checklist-key]")
      const documentsStatus = documentsLi?.querySelector("[data-documents-check-status]")
      if (documentsStatus && documentsLi) {
        const uploadedAtIso = documentsLi.dataset.documentsUploadedAt || null
        documentsStatus.textContent = documentsStatusLabel(
          !documentsLi.classList.contains("is-disabled"),
          event.target.checked,
          uploadedAtIso
        )
        documentsStatus.className = event.target.checked
          ? "backoffice-status backoffice-status--open"
          : "backoffice-status backoffice-status--loaded"
      }

      if (
        event.target.matches('input[type="checkbox"][name*="documents_checklist"]') &&
        documentsLi &&
        !documentsLi.classList.contains("is-disabled")
      ) {
        const control = documentsLi.querySelector(".backoffice-content-checklist__control")
        if (control) {
          const isGuide = documentsLi.hasAttribute("data-guide")
          control.setAttribute(
            "data-tooltip",
            event.target.checked
              ? isGuide
                ? `Guide validé pour l'édition ${editionYearFrom(form)} ! 🎉`
                : documentConfirmedTooltip(editionYearFrom(form))
              : isGuide
                ? "Vérifiez le guide, puis cochez pour confirmer."
                : DOCUMENT_CHECKLIST_TOOLTIP
          )
          dismissChecklistTooltipUntilLeave(control)
        }
      }
    }
    const delay = event.target.type === "file" || event.target.type === "checkbox" ? 0 : 450
    scheduleSave(form, delay)
  })
}

// Champs hors <form> via attribut form="…" (ex. prestations) : une seule délégation
// document. Sinon chaque visite Turbolinks empile des listeners qui relancent des
// autosaves sur d’anciens formulaires détachés et réécrasent les coches.
const bindFormAssociatedAutosaveDelegation = () => {
  if (document.documentElement.dataset.formAssociatedAutosaveBound === "true") return
  document.documentElement.dataset.formAssociatedAutosaveBound = "true"

  const handleAssociatedField = (event) => {
    const field = event.target
    const formId = field?.getAttribute?.("form")
    if (!formId) return

    const form = document.getElementById(formId)
    if (!form?.matches?.("form[data-autosave]") || isManualSave(form)) return
    // Déjà géré par les listeners branchés sur le <form> lui-même.
    if (field.closest?.("form") === form) return

    if (confirmPricingChecklistForField(field, form)) {
      scheduleSave(form, 0)
      return
    }

    if (event.type === "change" && isSetupChecklistCheckbox(field)) {
      const control = field.closest(".backoffice-pricing-checklist__control")
      if (control) {
        syncPricingChecklistTooltip(control, form)
        dismissChecklistTooltipUntilLeave(control)
      }
      rememberChecklistOverride(field)
      syncPricingIntroNote(form)
    }

    scheduleSave(form, event.type === "change" && field.type === "checkbox" ? 0 : 450)
  }

  document.addEventListener("input", handleAssociatedField)
  document.addEventListener("change", handleAssociatedField)
}

const SETUP_CHECKLIST_MODE_KEY = "backoffice-setup-checklist-mode"
const LEGACY_SETUP_CHECKLIST_MODE_KEYS = [
  "backoffice-date-and-location-checklist-mode",
  "backoffice-payment-checklist-mode",
  "backoffice-amenities-checklist-mode",
]

const readSetupChecklistMode = () => {
  try {
    return window.sessionStorage.getItem(SETUP_CHECKLIST_MODE_KEY) === "1"
  } catch (_) {
    return false
  }
}

const writeSetupChecklistMode = (enabled) => {
  try {
    window.sessionStorage.setItem(SETUP_CHECKLIST_MODE_KEY, enabled ? "1" : "0")
    // Anciennes clés par étape : plus utilisées.
    LEGACY_SETUP_CHECKLIST_MODE_KEYS.forEach((key) => {
      window.localStorage.removeItem(key)
    })
  } catch (_) {
    // ignore storage errors (private mode, etc.)
  }
}

const clearSetupChecklistModeOnSignIn = () => {
  if (!document.querySelector(".sign_in__form")) return

  try {
    window.sessionStorage.removeItem(SETUP_CHECKLIST_MODE_KEY)
    LEGACY_SETUP_CHECKLIST_MODE_KEYS.forEach((key) => {
      window.localStorage.removeItem(key)
    })
  } catch (_) {
    // ignore
  }
}

const applySetupChecklistMode = (root, enabled) => {
  if (!root) return

  const step = root.dataset.setupChecklistStep
  const form = root.querySelector(".backoffice-form--checklistable")
  const toggle = document.querySelector(
    `[data-setup-checklist-mode][data-setup-checklist-step="${step}"]`
  )
  const toggleLabel = toggle?.closest(".backoffice-toggle")

  root.classList.toggle("is-checklist-mode", enabled)
  form?.classList.toggle("is-checklist-mode", enabled)
  if (toggle) toggle.checked = enabled
  toggleLabel?.classList.toggle("is-on", enabled)

  if (step === "amenities") {
    root.querySelectorAll("details.backoffice-amenity-group").forEach((details) => {
      if (enabled) {
        details.open = true
        details.dataset.checklistLocked = "true"
      } else {
        delete details.dataset.checklistLocked
      }
    })
  }

  if (step === "reminders") {
    root.querySelectorAll("details.backoffice-notification").forEach((details) => {
      if (enabled) {
        details.open = true
        details.dataset.checklistLocked = "true"
      } else {
        delete details.dataset.checklistLocked
      }
    })
  }

  writeSetupChecklistMode(enabled)
}

const bindAmenityChecklistLocks = () => {
  if (document.documentElement.dataset.amenityChecklistLocksBound === "true") return
  document.documentElement.dataset.amenityChecklistLocksBound = "true"

  document.addEventListener("click", (event) => {
    const summary = event.target.closest(
      "details.backoffice-amenity-group[data-checklist-locked] > summary, details.backoffice-notification[data-checklist-locked] > summary"
    )
    if (!summary) return
    event.preventDefault()
  })

  document.addEventListener("toggle", (event) => {
    const details = event.target
    if (!(details instanceof HTMLDetailsElement)) return
    if (details.dataset.checklistLocked !== "true") return
    if (!details.open) details.open = true
  }, true)
}

const bindSetupChecklistMode = (root) => {
  if (!root || root.dataset.setupChecklistBound === "true") return
  root.dataset.setupChecklistBound = "true"

  const step = root.dataset.setupChecklistStep
  const toggle = document.querySelector(
    `[data-setup-checklist-mode][data-setup-checklist-step="${step}"]`
  )
  if (!toggle) return

  const enabled = readSetupChecklistMode()
  applySetupChecklistMode(root, enabled)

  toggle.addEventListener("change", () => {
    applySetupChecklistMode(root, toggle.checked)
  })
}

export const initBackofficeAutosave = () => {
  clearSetupChecklistModeOnSignIn()
  document.querySelectorAll("form[data-autosave]").forEach(bindAutosaveForm)
  document.querySelectorAll("[data-setup-checklist-root]").forEach(bindSetupChecklistMode)
  bindFormAssociatedAutosaveDelegation()
  bindChecklistPointerGuard()
  bindAmenityChecklistLocks()
  bindDocumentsChecklistRefresh()
}
